/**
 * "How the last ones went" (place manager, unit 2): which of a place's events
 * to show and what each row says. The reads come in through {@link EventsApi},
 * so the paging and the counts are unit-testable without a network; the real
 * reads live in `place-events-api.ts`.
 *
 * Counts only: who came, the tables opened, the top three. Nothing over time
 * exists to read yet, so there is no trend here.
 *
 * Who sees what: `Season.events` lists only the events the viewer may see
 * (the same visibility rule as `Query.event`; it listed private events to
 * everyone until smiletime/smiletime#5310). The results read is still checked:
 * for a partner an event whose results read null is not theirs to see and is
 * dropped: from the rows and from the Events card's count. Staff reads never
 * filter by visibility, so for staff a null means the event is gone, and the
 * row stays, said as not read.
 */
import type { EventResultData, PlaceSeasonsData, SeasonEventPage } from "@/data/place-events-api";
import { faceOf, type Face } from "@/features/places/face";
import type { Load } from "@/features/places/use-places";

/** How many events the section shows. */
export const EVENTS_SHOWN = 5;
/** How many of an event's best players a row shows (`rankings(first:)`). */
export const TOP_SHOWN = 3;
/** Seasons the first read lists (`seasons(first:)`), newest by start. */
export const SEASONS_LISTED = 20;
/** Seasons whose events are read: the most recently running that have begun. */
export const SEASONS_READ = 3;
/** Events per page of a season's events (`events(first:)`). */
export const EVENTS_PAGE = 100;
/** Pages read per season at most (so 500 events). */
export const MAX_PAGES = 5;
/** The window the Events card counts over. */
export const LATELY_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

type Season = PlaceSeasonsData["seasons"]["nodes"][number];
type ListedEvent = SeasonEventPage["nodes"][number];
/** An event's state as the UI reads it (`Event.stateUIHint`). */
type EventState = ListedEvent["stateUIHint"];

/** The states of an event that has begun: running, winding down, over but not
 *  closed out, or finished. Waiting to start and the countdown do not count. */
const BEGUN: ReadonlySet<EventState> = new Set<EventState>(["started", "winding_down", "waiting_to_finish", "finished"]);

/** An event cancelled before it started: `cancelEvent` sets it "finished"
 *  with `endAt` the moment of cancelling, which is before its start (an event
 *  cancelled within a minute of its start is ended the normal way instead, and
 *  did begin). Holds after the scheduled start has passed, too. */
export function wasCancelled(event: { stateUIHint: EventState; startAt: number; endAt: number }): boolean {
  return event.stateUIHint === "finished" && event.endAt < event.startAt;
}

/** An event has begun when its state says so, its start time has passed, and
 *  it was not cancelled before it started. */
export function hasBegun(event: { stateUIHint: EventState; startAt: number; endAt: number }, now: number): boolean {
  return BEGUN.has(event.stateUIHint) && event.startAt <= now && !wasCancelled(event);
}

/** The start of the {@link LATELY_DAYS} window ending `now`. */
function windowStart(now: number): number {
  return now - LATELY_DAYS * DAY_MS;
}

/** True when the oldest of `events` (newest first) is still inside the window,
 *  so older events not yet read could be inside it too. */
function reachesWindow(events: readonly ListedEvent[], now: number): boolean {
  const oldest = events[events.length - 1];
  return oldest !== undefined && oldest.startAt >= windowStart(now);
}

/** True when the place has more seasons than the first read listed. */
function seasonsUnlisted(seasons: PlaceSeasonsData["seasons"]): boolean {
  return seasons.totalCount > seasons.nodes.length;
}

/** The reads this section makes (`place-events-api.ts`, with the viewer bound). */
export interface EventsApi {
  seasons(placeId: string): Promise<PlaceSeasonsData | null>;
  seasonEvents(seasonId: string, offset: number): Promise<SeasonEventPage | null>;
  /** The results of the events shown, in one read, in the order asked (null:
   *  not visible to this viewer, or gone). */
  results(eventIds: readonly string[]): Promise<(EventResultData | null)[]>;
}

/** One season's events as read: newest first, `total` the season's whole count. */
export interface SeasonRead {
  season: Season;
  events: ListedEvent[];
  total: number;
}

/** An event from a season's read, with that season's title. */
export interface SeasonEvent extends ListedEvent {
  season: string;
}

/** The seasons whose events are read: of those that have begun, the
 *  {@link SEASONS_READ} most recently running (latest end first), so a long
 *  season still running is read before short ones that ended since it began.
 *  A season still to come has no event that has begun. */
export function seasonsToRead(seasons: readonly Season[], now: number): Season[] {
  return seasons
    .filter((s) => s.startAt <= now)
    .sort((a, b) => b.endAt - a.endAt)
    .slice(0, SEASONS_READ);
}

/** Whether a season's events so far (newest first) call for the next page:
 *  fewer than {@link EVENTS_SHOWN} have begun, or the oldest one read is still
 *  inside the {@link LATELY_DAYS} window (the Events card needs the whole window). */
export function wantsMore(events: readonly ListedEvent[], now: number): boolean {
  return events.filter((e) => hasBegun(e, now)).length < EVENTS_SHOWN || reachesWindow(events, now);
}

/** Read one season's events a page at a time, past any still to come, until
 *  `more` (by default {@link wantsMore}) is satisfied, the season runs out, or
 *  {@link MAX_PAGES}. The schedule (`schedule.ts`) passes its own `more`.
 *
 *  Pages are by offset over a start-time order with no tiebreak, so events
 *  with equal start times can come back in a different order on each read: one
 *  can show on both sides of a page edge (kept once), or on neither. So when a
 *  page starts no earlier than the last page ended (the edge falls inside a
 *  run of equal start times, or the order moved between reads), that page is
 *  read again from one place earlier and the two are merged. */
export async function readSeason(
  season: Season,
  api: Pick<EventsApi, "seasonEvents">,
  now: number,
  more: (events: readonly ListedEvent[], now: number) => boolean = wantsMore,
): Promise<SeasonRead> {
  const events: ListedEvent[] = [];
  const seen = new Set<string>();
  const keep = (nodes: readonly ListedEvent[]) => {
    for (const e of nodes) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      events.push(e);
    }
  };
  let total = 0;
  let lastOfPrev: ListedEvent | undefined;
  for (let page = 0; page < MAX_PAGES; page++) {
    const offset = page * EVENTS_PAGE;
    const read = await api.seasonEvents(season.id, offset);
    if (!read) break;
    const first = read.nodes[0];
    if (lastOfPrev && first && first.startAt >= lastOfPrev.startAt) {
      const again = await api.seasonEvents(season.id, offset - 1);
      if (again) keep(again.nodes);
    }
    keep(read.nodes);
    // Newest first again after a merge (stable: equal start times keep read order).
    events.sort((a, b) => b.startAt - a.startAt);
    lastOfPrev = read.nodes[read.nodes.length - 1];
    total = read.totalCount;
    if ((page + 1) * EVENTS_PAGE >= total || read.nodes.length < EVENTS_PAGE || !more(events, now)) break;
  }
  return { season, events, total };
}

/** The last `n` events that have begun, across the seasons read, newest first
 *  by start time. Equal start times keep the seasons' own order. */
export function lastBegunEvents(reads: readonly SeasonRead[], now: number, n: number = EVENTS_SHOWN): SeasonEvent[] {
  return reads
    .flatMap((r) => r.events.filter((e) => hasBegun(e, now)).map((e) => ({ ...e, season: r.season.title })))
    .sort((a, b) => b.startAt - a.startAt)
    .slice(0, n);
}

/** Events begun in the last {@link LATELY_DAYS} days, for the Events card. `atLeast` when
 *  the count may be short: a season read stopped with its oldest event still
 *  inside the window, or a season not read (or not listed) could hold events
 *  inside it. */
export function eventsLately(
  seasons: PlaceSeasonsData["seasons"],
  reads: readonly SeasonRead[],
  now: number,
): { count: number; atLeast: boolean } {
  const since = windowStart(now);
  const count = reads.reduce((sum, r) => sum + r.events.filter((e) => hasBegun(e, now) && e.startAt >= since).length, 0);
  const read = new Set(reads.map((r) => r.season.id));
  const cutShort = reads.some((r) => r.events.length < r.total && reachesWindow(r.events, now));
  const unreadInWindow = seasons.nodes.some((s) => !read.has(s.id) && s.startAt <= now && s.endAt >= since);
  // Unlisted seasons started before every listed one but may still be running.
  return { count, atLeast: cutShort || unreadInWindow || seasonsUnlisted(seasons) };
}

/** One row of the section. A null count is drawn as a dash. */
export interface EventRow {
  id: string;
  title: string;
  startAt: number;
  season: string;
  /** "on now" or "just ended" while the event is not yet closed out; null once finished. */
  status: string | null;
  /** People who came; null until the event ends (the backend sets it then). */
  came: number | null;
  /** Tables the event opened; null when its results did not read. */
  tables: number | null;
  /** The top {@link TOP_SHOWN}, best first; null when the event has no leaderboard. */
  top: Face[] | null;
  /** True when the event's results did not read (the read failed; or, to
   *  staff, the event is gone). A partner never gets a row for an event not
   *  visible to them. */
  unread: boolean;
}

function statusOf(stateUIHint: EventState): string | null {
  if (stateUIHint === "started" || stateUIHint === "winding_down") return "on now";
  if (stateUIHint === "waiting_to_finish") return "just ended";
  return null;
}

/** A row from a listed event and its results; null when they did not read:
 *  the row keeps its name and time, dashes elsewhere. */
export function eventRow(event: SeasonEvent, result: EventResultData | null): EventRow {
  const base = { id: event.id, title: event.title, startAt: event.startAt, season: event.season, status: statusOf(event.stateUIHint) };
  if (!result) return { ...base, came: null, tables: null, top: null, unread: true };
  return {
    ...base,
    came: result.totalVisitorCount ?? null,
    tables: result.gatherings.length,
    top:
      result.leaderboard?.rankings.slice(0, TOP_SHOWN).map((r) => faceOf(r.user)) ?? null,
    unread: false,
  };
}

/** What the section and the Events card draw for one place. */
export interface PlaceEvents {
  /** The last {@link EVENTS_SHOWN} events begun, newest first. */
  rows: EventRow[];
  /** Events begun in the last {@link LATELY_DAYS} days (less those found not
   *  visible to this viewer; then `atLeast`, since the rest were not checked). */
  lately: { count: number; atLeast: boolean };
  /** Events dropped from the rows (and the count) as not visible to this
   *  viewer: private events, to a partner. Always 0 for staff. */
  notShown: number;
  /** Seasons whose events were read; all the place's seasons this viewer sees
   *  (0: no events reach the place here); `cut` when some seasons that have
   *  begun were not read (only the {@link SEASONS_READ} most recent are);
   *  `capped` when a season's read stopped at {@link MAX_PAGES} still short of
   *  what it wanted, so older events in it were not read. */
  seasons: { read: number; total: number; cut: boolean; capped: boolean };
}

/** The read and its rows when there are rows to draw; null while reading,
 *  when the read failed, or with none to show (no season, or none begun).
 *  Shared by How the last ones went and the Turnstile. */
export function eventRowsOf(events: Load<PlaceEvents | null>): { data: PlaceEvents; rows: EventRow[] } | null {
  const data = events.state === "ready" ? events.data : null;
  return data && data.seasons.total > 0 && data.rows.length > 0 ? { data, rows: data.rows } : null;
}

/** Who is reading: staff (super admin) see every event; anyone else only the
 *  events visible to them. */
export interface EventsViewer {
  isSuperAdmin: boolean;
}

/** One shown event's results: read (null: not visible or gone), or the
 *  results read failed. */
export type ShownResult = EventResultData | null | "failed";

/** The section's model from what was read: rows for `shown` (picked by
 *  {@link lastBegunEvents}), each with its results from `resultOf`. For a
 *  partner, an event whose results read null is not visible to them and is
 *  dropped from the rows and the count; for staff it is gone, and its row
 *  says its results did not read. */
export function placeEventsOf(
  seasons: PlaceSeasonsData,
  reads: readonly SeasonRead[],
  shown: readonly SeasonEvent[],
  resultOf: (eventId: string) => ShownResult,
  now: number,
  viewer: EventsViewer,
): PlaceEvents {
  const rows: EventRow[] = [];
  const dropped: SeasonEvent[] = [];
  for (const e of shown) {
    const result = resultOf(e.id);
    if (result === null && !viewer.isSuperAdmin) dropped.push(e);
    else rows.push(eventRow(e, result === "failed" ? null : result));
  }
  const lately = eventsLately(seasons.seasons, reads, now);
  const droppedLately = dropped.filter((e) => e.startAt >= windowStart(now)).length;
  return {
    rows,
    lately: dropped.length > 0 ? { count: lately.count - droppedLately, atLeast: true } : lately,
    notShown: dropped.length,
    seasons: {
      read: reads.length,
      total: seasons.seasons.totalCount,
      cut: seasonsUnlisted(seasons.seasons) || seasons.seasons.nodes.filter((s) => s.startAt <= now).length > reads.length,
      capped: reads.some((r) => r.events.length < r.total && wantsMore(r.events, now)),
    },
  };
}

/** Read how a place's last events went: its seasons, the events of the most
 *  recent that have begun (seasons side by side, each paging on its own), then
 *  the results of just the events shown, in one read. That read failing leaves
 *  every row with dashes; any other read failing fails the whole. Null when
 *  the place does not exist or is not visible. */
export async function readPlaceEvents(
  placeId: string,
  api: EventsApi,
  now: number,
  viewer: EventsViewer,
): Promise<PlaceEvents | null> {
  const seasons = await api.seasons(placeId);
  if (!seasons) return null;
  const reads = await Promise.all(seasonsToRead(seasons.seasons.nodes, now).map((s) => readSeason(s, api, now)));
  const shown = lastBegunEvents(reads, now);
  let results: Map<string, EventResultData | null> | null = null;
  if (shown.length > 0) {
    try {
      const read = await api.results(shown.map((e) => e.id));
      results = new Map(shown.map((e, i) => [e.id, read[i] ?? null]));
    } catch {
      results = null;
    }
  }
  return placeEventsOf(seasons, reads, shown, (id) => (results ? (results.get(id) ?? null) : "failed"), now, viewer);
}
